import * as yup from "yup";
import { useState } from "react";
import { Trans } from "react-i18next";
import { toast } from "react-toastify";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";

import Loaing from "../../icons/loaing";
import ConfirmModal from "../ConfirmModal";
import TextArea from "../event-selection/textArea";
import SelectElement from "../event-selection/selectElement";
import { closeReporting } from "../../services/dashboardService";
import DeletePopup from "./delete-popup";
import { UPDATE_VALUES } from "../../data/constants";
import { updateNothingToDeclare } from "../../services/nothingToDeclare.service";
import { NothingToDeclarePDF } from "./nothing-to-declare-pdf";
import AdminWrapper from "../misc/AdminWrapper";

export type updateNothingToDeclareTypes = {
  _id: string;
  __v: number;
  reporting: string;
  month: string;
  comment: string;
  printmonth: string;
  printcomment: string;
  reportingId: string;
  setOpen: (value: boolean) => void;
  updateRecord: (value: any) => void;
};

const schema = yup.object({
  month: yup.string().required(),
  comment: yup.string().required(),
});

const UpdateNothingToDeclare: React.FC<updateNothingToDeclareTypes> = ({
  reporting,
  reportingId,
  setOpen,
  updateRecord,
  ...props
}) => {
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfData, setPdfData] = useState<any>(undefined);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<updateNothingToDeclareTypes>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...props,
    },
  });

  const handleDownload: SubmitHandler<updateNothingToDeclareTypes> = async (
    data
  ) => {
    setPdfData(data);
  };

  const onSubmit: SubmitHandler<updateNothingToDeclareTypes> = async (data) => {
    setLoading(true);
    try {
      const { printcomment, printmonth, ...rest } = data;
      const response = await updateNothingToDeclare(rest, data._id);
      if (response.message === "updated") {
        toast.success("Your action has been successfully saved!");
        setLoading(false);
        setOpen(false);
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };

  const resetForm = () => {
    setConfirmModalVisible(false);
    reset();
  };

  const updateReporting = async (value: string) => {
    setLoading(true);
    try {
      const response = await closeReporting(reportingId, { status: value });
      if (response.message === "updated") {
        toast.success("Your action has been successfully saved!");
        setLoading(false);
        updateRecord(response.data);
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };

  const deleteEvent = (deleteConfirmed: boolean) => {
    if (deleteConfirmed) {
      setOpenDeletePopup(false);
      updateReporting(UPDATE_VALUES.DELETED);
    } else {
      setOpenDeletePopup(false);
    }
  };
  return (
    <section className="relative font-Poppins">
      <AdminWrapper>
        <DeletePopup
          deleteEvent={deleteEvent}
          open={openDeletePopup}
          setOpen={setOpenDeletePopup}
        />
      </AdminWrapper>
      <div>
        <div className="grid gap-3 lg:mx-10">
          <div className="w-16 h-16">
            <img
              src="/images/report.png"
              alt="!"
              className="max-w-full h-auto"
            />
          </div>
          <h1 className="text-4xl font-bold">
            <Trans i18nKey="Nothing to declare">Nothing to declare</Trans>
          </h1>
          <p className="text-xl text-[#6B6B6B]">
            <Trans i18nKey="nothing_to_declare_form_message">
              This form is to be completed at the end of the month, only if you
              have no other events to report for that same month.
            </Trans>
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-end lg:px-[20px] my-10 gap-[15px]">
          <AdminWrapper>
            {editEnabled && (
              <button
                type="button"
                className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
                onClick={() => setConfirmModalVisible(true)}
              >
                <Trans>Reset</Trans>
              </button>
            )}

            {!editEnabled && (
              <button
                type="button"
                className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
                onClick={() => setEditEnabled(true)}
              >
                <Trans>Edit</Trans>
              </button>
            )}

            <button
              type="button"
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={() => setOpenDeletePopup(true)}
            >
              <Trans>Delete</Trans>
            </button>
            <button
              type="button"
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={() => updateReporting(UPDATE_VALUES.CLOSED)}
            >
              <Trans>Validate</Trans>
            </button>
          </AdminWrapper>
          {pdfData ? (
            <PDFDownloadLink
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              document={<NothingToDeclarePDF formData={pdfData} />}
              fileName={"nothing-to-declare.pdf"}
            >
              {({ blob, url, loading, error }) =>
                loading ? "Loading..." : "Download PDF"
              }
            </PDFDownloadLink>
          ) : (
            <button
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={handleSubmit(handleDownload)}
            >
              <Trans>Generate PDF</Trans>
            </button>
          )}
        </div>

        <div className="relative z-0 border border-gray-500 lg:mx-5">
          <div className="absolute -top-3 left-5 lg:-top-5 lg:left-14 bg-[white] px-[20px] lg:px-5">
            <h1 className="text-sm lg:text-3xl font-bold text-[#1E408E]">
              <Trans i18nKey="Event Details">Event Details</Trans>
            </h1>
          </div>
          <div className="md:p-16 sm:p-8 p-4 pt-16">
            <div className="grid gap-5">
              <SelectElement
                register={register}
                errors={errors}
                name="month"
                printEnabled
                label="I declare that I have no events to report for the month of:"
                options={[
                  "january",
                  "february",
                  "march",
                  "april",
                  "may",
                  "june",
                  "july",
                  "august",
                  "september",
                  "october",
                  "november",
                  "december",
                ]}
                translateId="I declare that I have no events to report for the month of:"
              />

              <TextArea
                register={register}
                errors={errors}
                name="comment"
                label="nothing_comment"
                printEnabled
              />
            </div>
          </div>
        </div>
        <AdminWrapper>
          <div className="flex justify-end lg:px-[20px] my-10">
            <button
              type={"submit"}
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
            >
              <Trans i18nKey="Submit">Submit</Trans>
            </button>
            {loading && <Loaing />}
          </div>
        </AdminWrapper>
      </form>
      <ConfirmModal
        isOpen={confirmModalVisible}
        setIsOpen={setConfirmModalVisible}
        onConfirm={resetForm}
      />
    </section>
  );
};

export default UpdateNothingToDeclare;
